import {
  CANVAS_ELEMENT_TYPES,
  moveCanvasElement,
  resizeBoxElement,
} from "../nativeCanvasElements";
import {
  findArrowsBoundToShapes,
  getFixedPointForBinding,
  resolveArrowElement,
} from "../geometry/arrowBinding";

/**
 * "Lifted" elements are the in-gesture copies drawn on the overlay layer while
 * the static layer keeps their committed versions hidden. This keeps the
 * static canvas untouched for the whole gesture.
 * @typedef {Map<string, object>} LiftedElements
 */

const createLiftedResolver = ({ elements, lifted }) => {
  const elementsById = new Map(elements.map((element) => [element.id, element]));

  return (elementId) => lifted.get(elementId) ?? elementsById.get(elementId);
};

const resolveArrowsAgainstLifted = ({ elements, lifted, movedIds }) => {
  const resolveElementById = createLiftedResolver({ elements, lifted });
  const boundArrows = findArrowsBoundToShapes({ elements, shapeIds: movedIds });

  boundArrows.forEach((arrow) => {
    const liftedArrow = lifted.get(arrow.id) ?? arrow;
    lifted.set(
      arrow.id,
      resolveArrowElement({ arrow: liftedArrow, resolveElementById })
    );
  });
  lifted.forEach((element, elementId) => {
    if (element.type !== CANVAS_ELEMENT_TYPES.ARROW || !element.bindings) return;
    lifted.set(
      elementId,
      resolveArrowElement({ arrow: element, resolveElementById })
    );
  });
};

/**
 * Moves the selected elements by `delta`. Bindings survive the move: bound
 * arrow endpoints re-pin to their shapes (stationary or moving along) during
 * the live resolve, so arrows pivot around their attachments instead of
 * detaching. Arrows bound to moved shapes re-route live as well.
 * @param {{ delta: { x: number, y: number }, elements: Array<object>, originals: Array<object> }} params
 * @returns {LiftedElements}
 */
export const buildMovedLifted = ({ delta, elements, originals }) => {
  const movedIds = new Set(originals.map((element) => element.id));
  const lifted = new Map();

  originals.forEach((original) => {
    lifted.set(original.id, moveCanvasElement({ delta, element: original }));
  });
  resolveArrowsAgainstLifted({ elements, lifted, movedIds });

  return lifted;
};

/**
 * Resizes one box element from a handle; arrows bound to it re-route live.
 * @param {{ elements: Array<object>, handle: string, original: object, point: { x: number, y: number } }} params
 * @returns {LiftedElements}
 */
export const buildResizedLifted = ({ elements, handle, original, point }) => {
  const lifted = new Map([
    [original.id, resizeBoxElement({ element: original, handle, point })],
  ]);
  resolveArrowsAgainstLifted({
    elements,
    lifted,
    movedIds: new Set([original.id]),
  });

  return lifted;
};

/**
 * Drags one arrow endpoint, optionally binding it to `bindTarget`.
 * @param {{ bindGap: number, bindTarget: object | null, elements: Array<object>, endpoint: "start" | "end", original: object, point: { x: number, y: number } }} params
 * @returns {LiftedElements}
 */
export const buildArrowEndpointLifted = ({
  bindGap,
  bindTarget,
  elements,
  endpoint,
  original,
  point,
}) => {
  const otherEndpoint =
    endpoint === "start"
      ? { x: original.x2, y: original.y2 }
      : { x: original.x1, y: original.y1 };
  const binding = bindTarget
    ? {
        elementId: bindTarget.id,
        fixedPoint: getFixedPointForBinding({
          element: bindTarget,
          point,
          towardFallback: otherEndpoint,
        }),
        gap: bindGap,
      }
    : undefined;
  const bindings = { ...original.bindings, [endpoint]: binding };
  const movedArrow =
    endpoint === "start"
      ? { ...original, bindings, x1: point.x, y1: point.y }
      : { ...original, bindings, x2: point.x, y2: point.y };
  const lifted = new Map([[original.id, movedArrow]]);
  const resolveElementById = createLiftedResolver({ elements, lifted });

  lifted.set(
    original.id,
    resolveArrowElement({ arrow: movedArrow, resolveElementById })
  );

  return lifted;
};

/**
 * @param {{ elements: Array<object>, lifted: LiftedElements }} params
 * @returns {Array<object>}
 */
export const applyLiftedElements = ({ elements, lifted }) =>
  elements.map((element) => lifted.get(element.id) ?? element);
